/**
 * 3DConnexion SpaceMouse — Type definitions
 *
 * Tipos para la integración con el ratón 3D de 3DConnexion.
 * El SDK no expone datos crudos del sensor; la navlib calcula matrices
 * de cámara. Estos tipos representan los deltas extraídos entre frames.
 */

// ---------------------------------------------------------------------------
// Motion data
// ---------------------------------------------------------------------------

/** Datos de movimiento extraídos entre frames consecutivos del navlib */
export interface SpaceMouseMotionData {
  /** Delta de traslación en eje X (derecha/izquierda) */
  dx: number;
  /** Delta de traslación en eje Y (arriba/abajo) */
  dy: number;
  /** Delta de traslación en eje Z (adelante/atrás — zoom) */
  dz: number;
  /** Delta de rotación en eje X (pitch) en radianes */
  rx: number;
  /** Delta de rotación en eje Y (yaw) en radianes */
  ry: number;
  /** Delta de rotación en eje Z (roll) en radianes */
  rz: number;
  /** Timestamp del frame (ms) */
  timestamp: number;
}

// ---------------------------------------------------------------------------
// Button events
// ---------------------------------------------------------------------------

export interface SpaceMouseButtonEvent {
  /** Identificador del botón o comando (ej: 'V3DK_FIT', 'ID_OPEN', etc.) */
  button: string;
  /** Tipo de evento */
  type: 'press' | 'release' | 'command';
}

// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------

export type SpaceMouseConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error';

// ---------------------------------------------------------------------------
// Event map (para EventEmitter tipado)
// ---------------------------------------------------------------------------

export interface SpaceMouseEvents {
  /** Se emite cada frame con los deltas de movimiento */
  motion: SpaceMouseMotionData;
  /** Se emite al pulsar/soltar un botón del dispositivo */
  button: SpaceMouseButtonEvent;
  /** Se emite cuando cambia el estado de conexión */
  connectionChange: SpaceMouseConnectionState;
  /** Se emite cuando se detecta/pierde el driver (checkAvailability) */
  availabilityChange: boolean;
  /** Se emite cuando el ratón empieza a moverse */
  motionStart: void;
  /** Se emite cuando el ratón deja de moverse */
  motionStop: void;
  /** Errores internos */
  error: Error;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface SpaceMouseConfig {
  /** Nombre de la aplicación mostrado en el panel de 3DConnexion */
  appName?: string;
  /** Intervalo de polling para checkAvailability (ms). 0 = sin polling */
  pollInterval?: number;
  /** Conectar automáticamente cuando se detecte el driver */
  autoConnect?: boolean;
  /** Habilitar logs de debug en consola */
  debug?: boolean;
}

export const DEFAULT_SPACEMOUSE_CONFIG: Readonly<SpaceMouseConfig> = {
  appName: 'Sippar',
  pollInterval: 5000,
  autoConnect: true,
  debug: false,
};
